/**
 * Inferred Mastery — FG3 (Architectural Peer Review 2026)
 *
 * THE PROBLEM
 * ===========
 * `prerequisiteCheck.ts` blocks a learner on an intra-band hard prerequisite
 * when no observed attempt history exists for that family yet. The classic
 * cold-start: no data ⇒ no mastery ⇒ blocked.
 *
 * The v11.7 `auto-pass for lower-group prereqs` only handles cross-group
 * fundaments (e.g. a Group 8 starter on a Group 6 family). Within the
 * learner's own group, hard prereqs still gate strictly — yet a Group 8
 * starter placed at M8 has been judged to *probably know* M8-level
 * material. We carry that judgement implicitly via the starting tier; we
 * never turn it into a per-family soft signal that `prereqCheck` can read.
 *
 * THE CONTRACT
 * ============
 * `InferredMasteryEntry` is a SEPARATE LAYER from `FamilyMasteryState`.
 * Observed mastery is the source of truth for adaptive selection; inferred
 * mastery is a soft, decaying confidence-weighted signal used ONLY by
 * `prereqCheck` (and later: `questionSelector` for probe injection).
 *
 * Anti-bandaid principles:
 *   #2 — `source` is an enum, not a boolean. We can SEE why a prereq passed.
 *   #5 — every inferred entry carries an explicit `confidence` (0..1).
 *         Downstream consumers choose their threshold; this module never
 *         pretends to know with certainty.
 *
 * DESIGN NOTES
 * ============
 * - Pure functions only (the actual storage lives in app store / data layer,
 *   wired in a later step). This module owns the TYPES and the
 *   READ/WRITE/SEED logic, not the persistence.
 * - The seed is keyed by `(familyId, group)` like observed mastery — same
 *   group-isolation contract.
 * - `confidence` defaults to 0.3 per the plan (Audit 3). Conservative start;
 *   can be tuned upward once shadow data confirms it doesn't auto-unlock
 *   genuinely unknown material.
 */

import type { FamilyMasteryState } from './familyMasteryEngine'
import { getFamilyMasteryKey } from './familyMasteryEngine'

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Why was this inferred mastery seeded? Surfaces in dashboards + analytics
 * so the team can trace cold-start unlock decisions back to a cause.
 */
export type InferredMasterySource =
  /** Auto-placement: learner was placed at this tier by date-based phase. */
  | 'auto-placement'
  /** Future: dedicated placement test produced this estimate. */
  | 'placement-test'
  /** Future: teacher manually marked the prereq as "probably knows it". */
  | 'teacher-attestation'

/**
 * A single inferred mastery entry. NOT a `FamilyMasteryState` — this is a
 * soft signal, intentionally lighter to make the semantic difference
 * impossible to miss.
 */
export interface InferredMasteryEntry {
  familyId: string
  group: number
  /**
   * Mastery score we INFER the learner already has (0..100). Treat as a
   * pessimistic estimate — the confidence field is the safety net.
   */
  score: number
  /**
   * 0..1 confidence in this inferred estimate. The default seed value is
   * `DEFAULT_INFERRED_CONFIDENCE` (0.3); higher confidence requires
   * stronger evidence (placement test, teacher attestation).
   */
  confidence: number
  source: InferredMasterySource
  /** Free-text breadcrumb so the trail is auditable in the UI. */
  basis: string
  /** Epoch ms when this entry was seeded. Used for decay later. */
  seededAt: number
}

/**
 * Default confidence for an auto-placement seed. Audit 3's recommendation.
 * Kept conservative — `prereqCheck` requires the score to clear the prereq
 * threshold AND the confidence to clear `MIN_CONFIDENCE_FOR_PASS`.
 */
export const DEFAULT_INFERRED_CONFIDENCE = 0.3

/**
 * Minimum confidence required for `prereqCheck` to honour an inferred-pass.
 * At or below this floor, the prereq is treated as `soft-pending` instead —
 * the selector then has the option to inject a probe question.
 */
export const MIN_CONFIDENCE_FOR_PASS = 0.25

// ─── Pure helpers (no I/O) ────────────────────────────────────────────────────

/**
 * Look up an inferred entry for a (familyId, group) pair. Same key contract
 * as `getFamilyMasteryKey` — both layers can be addressed by the same key
 * shape, but they live in different stores.
 */
export function getInferredMastery(
  inferredStore: Record<string, InferredMasteryEntry>,
  familyId: string,
  group: number,
): InferredMasteryEntry | undefined {
  return inferredStore[getFamilyMasteryKey(familyId, group)]
}

/**
 * Decide whether an inferred entry is "good enough" to be honoured as a
 * prereq pass. Two gates: the inferred score crosses the same threshold
 * the prereq uses, AND confidence clears the floor.
 *
 * NOTE: caller (prereqCheck) decides whether OBSERVED mastery should be
 * consulted first. Inferred is the fallback when observed is absent or
 * insufficient.
 */
export function isInferredPassing(
  entry: InferredMasteryEntry | undefined,
  threshold: number,
): boolean {
  if (!entry) return false
  if (entry.confidence < MIN_CONFIDENCE_FOR_PASS) return false
  return entry.score >= threshold
}

/**
 * Build a single inferred entry from an auto-placement decision. Pure —
 * the caller decides where to persist it.
 *
 * `placedTier` is the learner's starting tier from `getAutoStartingTier`.
 * `familyTier` is the prereq family's tier. We infer that families AT or
 * BELOW the placed tier are "probably known" with the default confidence;
 * families above get no inferred signal (no false reassurance).
 *
 * The inferred score is a soft 70 — above the standard
 * LDG_PREREQUISITE_MASTERY threshold (60) so it passes if confidence is
 * honoured, but well below 100 so the engine never mistakes it for
 * demonstrated mastery.
 */
export function buildPlacementInferredEntry(args: {
  familyId: string
  group: number
  familyTier: number
  placedTier: number
  now?: number
}): InferredMasteryEntry | undefined {
  const { familyId, group, familyTier, placedTier } = args
  if (familyTier > placedTier) return undefined
  return {
    familyId,
    group,
    score: 70,
    confidence: DEFAULT_INFERRED_CONFIDENCE,
    source: 'auto-placement',
    basis: `Auto-placement at tier ${placedTier} (≥ family tier ${familyTier})`,
    seededAt: args.now ?? Date.now(),
  }
}

/**
 * Combine an observed FamilyMasteryState (if any) with an inferred entry
 * (if any) into a single read-only view. Observed wins when present; the
 * inferred entry is consulted only as a fallback. Used by `prereqCheck`
 * to decide which `PrereqStatus` to emit.
 *
 * Returns a discriminated union so the caller cannot conflate "observed
 * passed" with "inferred passed" by accident.
 */
export type ResolvedMastery =
  | { kind: 'observed'; score: number; state: FamilyMasteryState }
  | { kind: 'inferred'; score: number; entry: InferredMasteryEntry }
  | { kind: 'none' }

export function resolveMasterySignal(
  observed: FamilyMasteryState | undefined,
  inferred: InferredMasteryEntry | undefined,
): ResolvedMastery {
  if (observed && observed.attempts > 0) {
    return { kind: 'observed', score: observed.masteryScore, state: observed }
  }
  if (inferred) {
    return { kind: 'inferred', score: inferred.score, entry: inferred }
  }
  return { kind: 'none' }
}
